/**
 * Element catalog accessors. The JSON is the schema authority for the whole
 * feature — element ids, ordering, category and which elements a clinic manager
 * is allowed to switch off.
 *
 * `elementId` values are Firestore map keys under
 * `visitStartSessions.elementAnswers`. Renaming one orphans existing data.
 *
 * Changed from the source: dropped the category helpers used only by the
 * registration desk, and the `photo-id` special case (the screen is deleted;
 * INJECTED_ELEMENT_DISPLAY went with it, so the catalog is now the ONLY source of
 * element ids and there is no second place to look for display copy).
 */

import i18n from "i18n";
import visitStartElementsCatalog from "./visitStartElements.json";

export const VISIT_START_TCI_ID = "visit-start";

export function getVisitStartElements() {
  return Array.isArray(visitStartElementsCatalog?.elements)
    ? visitStartElementsCatalog.elements
    : [];
}

/** Elements a clinic manager may NOT switch off. */
export function getVisitStartCoreElements() {
  return getVisitStartElements().filter(
    (element) =>
      element?.managerConfigurable === false || element?.tier === "core",
  );
}

export function getVisitStartConfigurableElements() {
  return getVisitStartElements().filter(
    (element) => element?.managerConfigurable === true,
  );
}

export function getVisitStartCoreElementIds() {
  return getVisitStartCoreElements()
    .map((element) => String(element?.elementId || "").trim())
    .filter(Boolean);
}

export function getVisitStartConfigurableElementIds() {
  return getVisitStartConfigurableElements()
    .map((element) => String(element?.elementId || "").trim())
    .filter(Boolean);
}

/** All core elements + configurable ones marked defaultEnabled. */
export function defaultVisitStartEnabledElements() {
  return getVisitStartElements()
    .filter(
      (element) =>
        element?.tier === "core" ||
        element?.managerConfigurable === false ||
        element?.defaultEnabled === true,
    )
    .map((element) => String(element?.elementId || "").trim())
    .filter(Boolean);
}

/**
 * Build an enabled set from a manager's selections. Core ids are always
 * re-added — never let the client omit them.
 */
export function buildEnabledElements(configurableIds = []) {
  const allowedConfigurable = new Set(getVisitStartConfigurableElementIds());
  const selected = [];
  const seen = new Set();

  getVisitStartCoreElementIds().forEach((elementId) => {
    if (seen.has(elementId)) return;
    seen.add(elementId);
    selected.push(elementId);
  });

  (Array.isArray(configurableIds) ? configurableIds : []).forEach((rawId) => {
    const elementId = String(rawId || "").trim();
    if (!elementId || !allowedConfigurable.has(elementId) || seen.has(elementId))
      return;
    seen.add(elementId);
    selected.push(elementId);
  });

  return selected;
}

/**
 * Resolve the enabled set for a session.
 *
 * `null` means no profile — a clinic that has never opened the command center, or
 * an unscoped session with no clinic at all — and yields the catalog defaults.
 * A profile from `locations/{id}/visitStartConfig/profile` yields its own set.
 *
 * The test is whether `enabledElements` is an ARRAY, not whether it is a non-empty
 * one. A clinic that switches off every configurable element has made a real
 * choice — core elements only — and buildEnabledElements still re-adds the core
 * ids, so the result is a valid questionnaire. Treating empty as "unset" and
 * handing back the defaults would silently restore the questions they had just
 * turned off, and the command center would appear not to save.
 *
 * @param {{ enabledElements?: string[] }|null} profile
 * @returns {string[]}
 */
export function resolveEnabledElements(profile) {
  if (Array.isArray(profile?.enabledElements)) {
    return buildEnabledElements(
      profile.enabledElements.filter((id) =>
        getVisitStartConfigurableElementIds().includes(id),
      ),
    );
  }
  return defaultVisitStartEnabledElements();
}

/**
 * Display metadata for one element. Falls back to the full `label` when a row
 * has no `shortLabel`, so a newly added element renders before its copy lands.
 */
export function getVisitStartElementDisplay(elementId) {
  const row = getVisitStartElements().find(
    (element) => String(element?.elementId || "").trim() === elementId,
  );
  return {
    // Administrative name, used by governance surfaces — not shown to patients.
    label: row?.label || elementId,
    // Stepper.
    shortLabel: row?.shortLabel || row?.label || elementId,
    // h4 hero.
    patientTitle: row?.patientTitle || row?.label || elementId,
    // "Why we ask", under the question.
    patientDescription: row?.patientDescription || "",
  };
}

/**
 * The patient-facing half of getVisitStartElementDisplay, in the patient's
 * language. The catalog JSON stays the English source (and what the clinic-side
 * summary reads); translations live in the visitStart namespace under
 * elements.<elementId>, falling back to the catalog copy for a row with none.
 */
export function getVisitStartElementPatientDisplay(elementId) {
  const display = getVisitStartElementDisplay(elementId);
  const translate = (field) =>
    display[field]
      ? i18n.t(`visitStart:elements.${elementId}.${field}`, {
          defaultValue: display[field],
        })
      : display[field];
  return {
    shortLabel: translate("shortLabel"),
    patientTitle: translate("patientTitle"),
    patientDescription: translate("patientDescription"),
  };
}
